import { DPDay, useDatePicker } from '@rehookify/datepicker';
import { VpButton, VpDivider } from '@vtmn-play/react';
import clsx from 'clsx';
import { format, getWeek, subDays } from 'date-fns';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, CalendarGrid, Section, SectionHeader } from '..';
import { CalendarProps } from './CalendarProps';
import styles from './styles.module.scss';

export const Calendar = ({
  minSelectableDate,
  maxSelectableDate,
  selectedDate,
  setIsPopoverOpen,
  onChangeDate,
}: CalendarProps) => {
  const [selectedDates, onDatesChange] = useState<Date[]>(
    selectedDate.length > 0 ? selectedDate : [subDays(minSelectableDate[0], 1)],
  );
  const [weeksNumber, setWeeksNumber] = useState<(number | string)[]>();

  const {
    data: { calendars, weekDays },
    propGetters: { dayButton, addOffset, subtractOffset },
  } = useDatePicker({
    selectedDates,
    onDatesChange,
    calendar: {
      startDay: 0,
    },
  });

  const { t } = useTranslation();
  const { month, year, days } = calendars[0];

  const calculateWeekNumber = () => {
    const weeksNumberArray: (number | string)[] = ['W'];
    days.forEach((d) => {
      const weeknumber = getWeek(d.$date, {
        firstWeekContainsDate: 4,
        weekStartsOn: 1,
      });
      if (weeksNumberArray && weeksNumberArray?.indexOf(weeknumber) < 0) {
        weeksNumberArray.push(weeknumber);
      }
    });
    weeksNumberArray.splice(1, 1);
    setWeeksNumber(weeksNumberArray);
  };

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    calculateWeekNumber();
  }, [month, year]);

  const isNotInCurrentMonth = (day: DPDay) => {
    return month !== format(day.$date, 'MMMM');
  };

  const selectableDays = (day: DPDay) => {
    const isBeforeMinSelectableDate =
      day.$date < subDays(minSelectableDate[0], 1);
    const isAfterMaxSelectableDate = day.$date > maxSelectableDate[0];

    return (
      isBeforeMinSelectableDate ||
      isNotInCurrentMonth(day) ||
      isAfterMaxSelectableDate
    );
  };

  return (
    <div
      data-testid="calendar_component"
      className={styles['spo-calendar-wrapper']}
    >
      <main className="vtmn-flex">
        <div
          className={`${styles['spo-calendar']} vtmn-bg-white vtmn-py-2 vtmn-px-4 vtmn-rounded-lg`}
        >
          <div className="vtmn-flex vtmn-justify-start">
            <SectionHeader>
              <Button
                data-testid="calendar_previous-month-button"
                {...subtractOffset({ months: 1 })}
              >
                <span className="vtmx-chevron-left-line" />
              </Button>
              <p
                data-testid="calendar_month-label"
                className="vtmn-text-center vtmn-text-sm vtmn-mx-2 vtmn-w-10"
              >
                {month}
              </p>
              <Button
                data-testid="calendar_next-month-button"
                {...addOffset({ months: 1 })}
              >
                <span className="vtmx-chevron-right-line" />
              </Button>
            </SectionHeader>
            <SectionHeader>
              <Button
                data-testid="calendar_previous-year-button"
                {...subtractOffset({ years: 1 })}
              >
                <span className="vtmx-chevron-left-line" />
              </Button>
              <p
                data-testid="calendar_year-label"
                className="vtmn-text-center vtmn-text-sm vtmn-mx-2"
              >
                {year}
              </p>
              <Button
                data-testid="calendar_next-year-button"
                {...addOffset({ years: 1 })}
              >
                <span className="vtmx-chevron-right-line" />
              </Button>
            </SectionHeader>
          </div>
          <div className="vtmn-flex">
            <div
              data-testid="calendar_weeks_container"
              className={`${styles['spo-weeks-number']} vtmn-grid vtmn-grid-rows-7 vtmn-gap-y-1 vtmn-w-6 vtmn-text-xs vtmn-items-end vtmn-mr-1 vtmn-rounded vtmn-items-center`}
            >
              {weeksNumber?.map((w) => (
                <p
                  data-testid={`calendar_week_number-${w}`}
                  key={w}
                  className="vtmn-font-bold vtmn-text-center"
                >
                  {w}
                </p>
              ))}
            </div>
            <Section>
              <CalendarGrid
                dataTestId="calendar_week-days_container"
                className="vtmn-mb-2 vtmn-items-center vtmn-h-6"
              >
                {weekDays.map((d) => (
                  <p
                    key={d}
                    data-testid={`calendar_weekday-${d}`}
                    className="vtmn-text-xs vtmn-text-center"
                  >
                    {d}
                  </p>
                ))}
              </CalendarGrid>
              <CalendarGrid dataTestId="calendar_days_container">
                {days.map((d) => {
                  d = { ...d, disabled: selectableDays(d) };
                  return (
                    <Button
                      data-testid={`calendar_day-${d.day}${
                        !isNotInCurrentMonth(d) ? `_current-month` : ''
                      }`}
                      key={d.$date.toString()}
                      className={clsx(
                        'vtmn-w-6',
                        'vtmn-text-xs',
                        styles['spo-day'],
                        {
                          [`${styles['spo-day-selected-disabled']}`]:
                            d.disabled,
                        },
                        {
                          [`${styles['spo-day-selected']}`]: d.selected,
                        },
                      )}
                      {...dayButton(d)}
                    >
                      {d.day}
                    </Button>
                  );
                })}
              </CalendarGrid>
            </Section>
          </div>
          <VpDivider className="spo-calendar-divider" />
          <div className="vtmn-flex vtmn-justify-end">
            <VpButton
              data-testid="calendar_save-button"
              shape="squared"
              className={styles['spo-save-button']}
              size="small"
              onClick={() => {
                onChangeDate(selectedDates);
                setIsPopoverOpen(false);
              }}
            >
              {t('save')}
            </VpButton>
          </div>
        </div>
      </main>
    </div>
  );
};
